import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import GeoSelectField from '../shared/GeoSelectField';
import { geoCascade } from '../shared/bdGeo';
import FamilyCardPreviewFrame from './FamilyCardPreviewFrame';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import FamilyCardFormInfoBox from './FamilyCardFormInfoBox';
import {
    INITIAL_FAMILY_CARD_FORM,
    RECIPIENT_VALUES,
    displayField,
    getEmptyFamilyCardFields,
    getFamilyCardFieldSuggestions,
    getFamilyCardFormSnapshot,
    getMobileValidationError,
    isFamilyCardFormComplete,
    normalizeBdMobile,
    normalizeDigits,
    saveFamilyCardFieldHistory,
    saveFamilyCardFormSnapshot,
} from './familyCardFormEngine';
import { downloadFamilyCardPdf, printFamilyCardForm } from './generateFamilyCardPdf';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-3.5 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {children}
        </div>
    );
}

function SuggestInput({
    id,
    name,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    suggestions = [],
    inputMode,
    type = 'text',
    maxLength,
    autoComplete,
    listId,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                inputMode={inputMode}
                type={type}
                maxLength={maxLength}
                autoComplete={autoComplete}
                list={listId}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass}`}
            />
            {listId && suggestions.length > 0 ? (
                <datalist id={listId}>
                    {suggestions.map((item) => (
                        <option key={item} value={item} />
                    ))}
                </datalist>
            ) : null}
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function FamilyCardFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_FAMILY_CARD_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [mobileError, setMobileError] = useState('');
    const [mobileTouched, setMobileTouched] = useState(false);
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getFamilyCardFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (
                displayField(next.applicantName)
                || displayField(next.fatherName)
                || displayField(next.motherName)
            ) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveFamilyCardFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            if (!displayField(value)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const handleDigitsChange = (key, raw, maxLength) => {
        update(key, normalizeDigits(raw, maxLength));
    };

    const handleMobileChange = (raw) => {
        const digits = normalizeBdMobile(raw);
        update('mobile', digits);
        if (mobileTouched) {
            setMobileError(getMobileValidationError(digits, t));
        }
    };

    const validateMobile = () => {
        setMobileTouched(true);
        if (!form.mobile) {
            setMobileError('');
            return true;
        }
        const err = getMobileValidationError(form.mobile, t);
        setMobileError(err);
        return !err;
    };

    const mobileInputError = fieldErrors.mobile || (mobileTouched ? mobileError : '');

    /**
     * Remember what was typed, then export — whatever state the form is in.
     *
     * Missing fields deliberately do NOT block the download: a common use of
     * this tool is printing the form blank, or half-filled, to complete the rest
     * by hand at the office. Downloading one only asks for a confirmation that
     * names what is still empty, and printing does not even ask.
     */
    const prepareForExport = () => {
        setFieldErrors({});
        saveFamilyCardFieldHistory('fatherName', form.fatherName);
        saveFamilyCardFieldHistory('motherName', form.motherName);
        saveFamilyCardFieldHistory('nid', form.nid);
        saveFamilyCardFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyFamilyCardFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('familyCardForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage('general/family-card-form', { category: 'general' });
            const base = form.applicantName.trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadFamilyCardPdf(previewRef.current, `family-card-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('familyCardForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('familyCardForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = () => {
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = async () => {
        recordToolUsage('general/family-card-form', { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('familyCardForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printFamilyCardForm(previewRef.current, t('familyCardForm.errors.popupBlocked'));
            notifications.show({
                title: t('familyCardForm.printTitle'),
                message: t('familyCardForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('familyCardForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_FAMILY_CARD_FORM);
        setMobileError('');
        setMobileTouched(false);
        setFieldErrors({});
        notifications.show({
            title: t('familyCardForm.clearedTitle'),
            message: t('familyCardForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getFamilyCardFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('familyCardForm.noSnapshotTitle'),
                message: t('familyCardForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        setMobileError('');
        setMobileTouched(false);
        setFieldErrors({});
        notifications.show({
            title: t('familyCardForm.restoredTitle'),
            message: t('familyCardForm.restoredMessage'),
            color: 'green',
        });
    };

    const hasAnyInput = useMemo(
        () => Object.entries(form).some(([key, value]) => key !== 'recipientType' && displayField(value)),
        [form],
    );

    const isComplete = useMemo(() => isFamilyCardFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('familyCardForm.statusReady'),
        success: t('familyCardForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`familyCardForm.howTo.step${i}`)),
        [t],
    );

    // District first, then its upazilas (see shared/bdGeo).
    const geo = geoCascade(form, update, { district: 'district', upazila: 'upazila' });

    const fieldError = (key) => {
        const err = fieldErrors[key];
        if (!err) return undefined;
        if (key === 'mobile') return err;
        return ' ';
    };

    const suggestionsFor = (key) => getFamilyCardFieldSuggestions(key);

    return (
        <>
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug="general/family-card-form"
            subtitleKey="familyCardForm.subtitle"
            // 30/70: the letter is what the applicant is checking as they
            // type, and an A4 page needs the room to stay legible, so the
            // fields take the narrow column and the preview becomes the
            // sticky rail beside them. The 9px is half the column gap.
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('familyCardForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('familyCardForm.clearAll')}
            primaryAction={{
                label: t('familyCardForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            // Print and Restore ride in the sticky bar with Download and Clear,
            // so every action on the form is in one place at the foot of the
            // screen rather than parked beside a column you have scrolled past.
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('familyCardForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('familyCardForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection number={t('familyCardForm.sections.general.number')} title={t('familyCardForm.sections.general.title')}>
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel>{t('familyCardForm.fields.recipientType')}</FormLabel>
                                    <Select
                                        value={form.recipientType}
                                        onValueChange={(v) => update('recipientType', v)}
                                    >
                                        <SelectTrigger className="h-[42px] rounded-xl">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {RECIPIENT_VALUES.map((value) => (
                                                <SelectItem key={value} value={value}>
                                                    {t(`familyCardForm.recipients.${value}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <FormLabel htmlFor="unionName">{t('familyCardForm.fields.unionName')}</FormLabel>
                                    <SuggestInput
                                        id="unionName"
                                        name="unionName"
                                        value={form.unionName}
                                        onChange={(v) => update('unionName', v)}
                                        placeholder={t('familyCardForm.placeholders.unionName')}
                                        error={fieldError('unionName')}
                                        suggestions={suggestionsFor('unionName')}
                                        listId="family-card-unionName"
                                        autoComplete="organization"
                                    />
                                </div>
                            </div>

                            <div>
                                <FormLabel htmlFor="applicantName">{t('familyCardForm.fields.applicantName')}</FormLabel>
                                <SuggestInput
                                    id="applicantName"
                                    name="applicantName"
                                    value={form.applicantName}
                                    onChange={(v) => update('applicantName', v)}
                                    placeholder={t('familyCardForm.placeholders.applicantName')}
                                    error={fieldError('applicantName')}
                                    suggestions={suggestionsFor('applicantName')}
                                    listId="family-card-applicantName"
                                    autoComplete="name"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="fatherName">{t('familyCardForm.fields.fatherName')}</FormLabel>
                                    <SuggestInput
                                        id="fatherName"
                                        name="fatherName"
                                        value={form.fatherName}
                                        onChange={(v) => update('fatherName', v)}
                                        placeholder={t('familyCardForm.placeholders.fatherName')}
                                        error={fieldError('fatherName')}
                                        suggestions={suggestionsFor('fatherName')}
                                        listId="family-card-fatherName"
                                        autoComplete="father-name"
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="motherName">{t('familyCardForm.fields.motherName')}</FormLabel>
                                    <SuggestInput
                                        id="motherName"
                                        name="motherName"
                                        value={form.motherName}
                                        onChange={(v) => update('motherName', v)}
                                        placeholder={t('familyCardForm.placeholders.motherName')}
                                        error={fieldError('motherName')}
                                        suggestions={suggestionsFor('motherName')}
                                        listId="family-card-motherName"
                                        autoComplete="mother-name"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="nid">{t('familyCardForm.fields.nid')}</FormLabel>
                                    <SuggestInput
                                        id="nid"
                                        name="nid"
                                        value={form.nid}
                                        onChange={(v) => handleDigitsChange('nid', v, 17)}
                                        placeholder={t('familyCardForm.placeholders.nid')}
                                        error={fieldError('nid')}
                                        suggestions={suggestionsFor('nid')}
                                        listId="family-card-nid"
                                        inputMode="numeric"
                                        maxLength={17}
                                        autoComplete="national-id"
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="mobile">{t('familyCardForm.fields.mobile')}</FormLabel>
                                    <SuggestInput
                                        id="mobile"
                                        name="mobile"
                                        value={form.mobile}
                                        onChange={handleMobileChange}
                                        onBlur={validateMobile}
                                        placeholder={t('familyCardForm.placeholders.mobile')}
                                        error={mobileInputError}
                                        inputMode="tel"
                                        type="tel"
                                        maxLength={11}
                                        autoComplete="tel"
                                    />
                                </div>
                            </div>
                        </div>
                    </FormSection>

                    <FormSection number={t('familyCardForm.sections.address.number')} title={t('familyCardForm.sections.address.title')}>
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="village">{t('familyCardForm.fields.village')}</FormLabel>
                                    <SuggestInput
                                        id="village"
                                        name="village"
                                        value={form.village}
                                        onChange={(v) => update('village', v)}
                                        placeholder={t('familyCardForm.placeholders.village')}
                                        error={fieldError('village')}
                                        suggestions={suggestionsFor('village')}
                                        listId="family-card-village"
                                        autoComplete="street-address"
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="postOffice">{t('familyCardForm.fields.postOffice')}</FormLabel>
                                    <SuggestInput
                                        id="postOffice"
                                        name="postOffice"
                                        value={form.postOffice}
                                        onChange={(v) => update('postOffice', v)}
                                        placeholder={t('familyCardForm.placeholders.postOffice')}
                                        error={fieldError('postOffice')}
                                        suggestions={suggestionsFor('postOffice')}
                                        listId="family-card-postOffice"
                                        autoComplete="address-line2"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="wardNo">{t('familyCardForm.fields.wardNo')}</FormLabel>
                                    <SuggestInput
                                        id="wardNo"
                                        name="wardNo"
                                        value={form.wardNo}
                                        onChange={(v) => handleDigitsChange('wardNo', v, 3)}
                                        placeholder={t('familyCardForm.placeholders.wardNo')}
                                        error={fieldError('wardNo')}
                                        suggestions={suggestionsFor('wardNo')}
                                        listId="family-card-wardNo"
                                        inputMode="numeric"
                                        maxLength={3}
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="district">{t('familyCardForm.fields.district')}</FormLabel>
                                    <GeoSelectField
                                        {...geo.district}
                                        selectOnly
                                        placeholder={t('familyCardForm.placeholders.district')}
                                        error={fieldError('district')}
                                        inputClassName={`${inputBaseClass} ${fieldError('district') ? inputErrClass : inputOkClass}`}
                                    />
                                </div>
                            </div>

                            <div>
                                <FormLabel htmlFor="upazila">{t('familyCardForm.fields.upazila')}</FormLabel>
                                <GeoSelectField
                                    {...geo.upazila}
                                    placeholder={t('familyCardForm.placeholders.upazila')}
                                    error={fieldError('upazila')}
                                    inputClassName={`${inputBaseClass} ${fieldError('upazila') ? inputErrClass : inputOkClass}`}
                                />
                            </div>
                        </div>
                    </FormSection>

                    <FormSection number={t('familyCardForm.sections.family.number')} title={t('familyCardForm.sections.family.title')}>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <FormLabel htmlFor="sonsCount">{t('familyCardForm.fields.sonsCount')}</FormLabel>
                                <SuggestInput
                                    id="sonsCount"
                                    name="sonsCount"
                                    value={form.sonsCount}
                                    onChange={(v) => handleDigitsChange('sonsCount', v, 2)}
                                    placeholder={t('familyCardForm.placeholders.sonsCount')}
                                    error={fieldError('sonsCount')}
                                    inputMode="numeric"
                                    maxLength={2}
                                />
                            </div>
                            <div>
                                <FormLabel htmlFor="daughtersCount">{t('familyCardForm.fields.daughtersCount')}</FormLabel>
                                <SuggestInput
                                    id="daughtersCount"
                                    name="daughtersCount"
                                    value={form.daughtersCount}
                                    onChange={(v) => handleDigitsChange('daughtersCount', v, 2)}
                                    placeholder={t('familyCardForm.placeholders.daughtersCount')}
                                    error={fieldError('daughtersCount')}
                                    inputMode="numeric"
                                    maxLength={2}
                                />
                            </div>
                        </div>
                    </FormSection>
                </div>
            )}
            preview={(
                // A flex column, not a plain block: the preview panel stretches
                // itself, and inside a block wrapper that height would resolve
                // against nothing.
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('familyCardForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('familyCardForm.previewEmpty')}
                        idleHint={t('familyCardForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        {/* Bounded, not min-height: a card taller than the screen
                            cannot stay pinned, so the letter scrolls inside it. */}
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('familyCardForm.livePreview')}
                            </div>
                            <FamilyCardPreviewFrame form={form} previewRef={previewRef} zoom={pageZoom.zoom} />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<FamilyCardFormInfoBox />}
        />
        </>
    );
}
